import React, { useState, useEffect, useRef } from "react";
import { Card, ListGroup, Form, Dropdown, Spinner } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import styles from "./Card.module.css";
import ResourceViewerModal from "../modals/ResourceViewerModal";
import { fetchPatient } from "../services/patientService";
import { useDispatch, useStore } from "react-redux";
import { Overlay, Tooltip } from "react-bootstrap";
import PatientFemaleIcon from "../assets/Patient_Female_96.png";
import PatientMaleIcon from "../assets/Patient_Male_96.png";

function PatientCard({ title, inContext, patientData, fhirClient, showSearch = false, onSelectPatient, isReply }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchedPatients, setSearchedPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(patientData || null);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalPatientDetails, setModalPatientDetails] = useState(null);
  const [searching, setSearching] = useState(false);
  const dispatch = useDispatch();
  const store = useStore();
  const [showTooltip, setShowTooltip] = useState(false);
  const iconRef = useRef(null);

  useEffect(() => {
    if (patientData?.name?.[0]) {
      const name = `${patientData.name[0].given?.join(" ") || ""} ${patientData.name[0].family || ""}`.trim();
      setSearchTerm(name);
    }
  }, [patientData]);

  const displayPatient = selectedPatient || patientData || {
    id: "N/A",
    name: [{ given: ["N/A"], family: "N/A" }],
    gender: "N/A",
    birthDate: "N/A",
    address: [{ line: ["N/A"], city: "N/A", state: "N/A", postalCode: "N/A" }],
    maritalStatus: { text: "N/A" },
    communication: [{ language: { text: "N/A" } }],
    telecom: [{ system: "phone", value: "N/A" }]
  };

  const handleModalClose = () => setShowModal(false);

  const handleModalOpen = async () => {
    if (!displayPatient.id) {
      setModalPatientDetails(null);
      setShowModal(true);
      return;
    }
    const patient = await fetchPatient(displayPatient.id, fhirClient, store);
    setModalPatientDetails(patient || null);
    setShowModal(true);
  };

  const handleSearchByName = async () => {
    if (!searchTerm.trim()) return setError("Please enter a patient name.");
    if (!fhirClient) return setError("FHIR Client is not initialized.");
    setSearching(true);
    try {
      const searchResults = await fhirClient.request(`Patient?name=${encodeURIComponent(searchTerm)}`);
      const found = searchResults.entry?.map(e => e.resource) || [];
      setSearchedPatients(found);
      setError(found.length ? "" : "No patients found.");
    } catch (err) {
      console.error("Error searching patient:", err);
      setError("Error searching patient.");
      setSearchedPatients([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectPatient = async (patient) => {
    const fullName = patient.name?.[0]?.given?.join(" ") + " " + patient.name?.[0]?.family;
    setSearchTerm(fullName);

    const fetched = await fetchPatient(patient.id, fhirClient, store);
    const resolvedPatient = fetched || patient;

    setSelectedPatient(resolvedPatient);
    setSearchedPatients([]);

    if (typeof onSelectPatient === "function") onSelectPatient(resolvedPatient);
  };

  const getPatientName = (patient) => {
    const nameObj = patient?.name?.[0];
    if (!nameObj) return "Patient";
    const given = nameObj.given?.join(" ") || "";
    const family = nameObj.family || "";
    return `${given} ${family}`.trim();
  };

  const patientAddress = displayPatient.address?.[0];
  const fullAddress = [
    ...(patientAddress?.line || []),
    patientAddress?.city,
    patientAddress?.state,
    patientAddress?.postalCode
  ].filter(Boolean).join(", ");

  const getMRN = (patient) => {
    const identifierArray = patient.identifier || [];
    const mrnIdentifier = identifierArray.find((id) =>
      id.type?.coding?.some((coding) => coding.code === "MR")
    );
    return mrnIdentifier?.value || "MRN not found";
  };

  const mrn = getMRN(displayPatient);

  // ✅ Gender-specific icon
  const gender = displayPatient.gender?.toLowerCase();
  const patientIcon = gender === "female" ? PatientFemaleIcon : PatientMaleIcon;

  return (
    <>
      <Card style={{ width: "100%", marginTop: "0px", minHeight: "250px" }}>
        <Card.Header
          className={
            `d-flex justify-content-between align-items-center ${inContext ? styles.inContextCardHeader : styles.cardHeader
            }`
          }
          style={{ cursor: "pointer" }}
          onClick={handleModalOpen}
        >
          {/* TITLE */}
          <h6 className="mb-0">
            <strong>{title}</strong>
          </h6>

          {/* RIGHT SIDE CONTENT (Search + Icon + Spinner) */}
          <div className="d-flex align-items-center">

            {/* SEARCH INPUT (does NOT trigger header click) */}
            {showSearch && !isReply && (
              <div
                style={{ position: "relative", marginRight: "10px" }}
                onClick={(e) => e.stopPropagation()}   // prevent header click
                onMouseEnter={(e) => e.stopPropagation()}
              >
                <Form.Control
                  type="text"
                  placeholder="Search Patient"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearchByName();
                    }
                  }}
                  style={{ width: "200px" }}
                />
                <FaSearch
                  style={{
                    cursor: "pointer",
                    position: "absolute",
                    right: "5px",
                    top: "50%",
                    transform: "translateY(-50%)"
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSearchByName();
                  }}
                />

                {/* DROPDOWN RESULTS */}
                {searchedPatients.length > 0 && (
                  <Dropdown.Menu
                    show
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      width: "350px",
                      zIndex: 1000,
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #ced4da",
                      boxShadow: "0px 4px 10px rgba(0,0,0,0.1)"
                    }}
                  >
                    {searchedPatients.map((patient) => (
                      <Dropdown.Item
                        key={patient.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectPatient(patient);
                        }}
                      >
                        <div>
                          <div>
                            <strong>
                              {patient.name?.[0]?.given?.join(" ")} {patient.name?.[0]?.family}
                            </strong>
                          </div>

                          <div className="text-muted small">
                            MRN: {getMRN(patient)}
                          </div>

                          <div className="text-muted small">
                            {patient.gender?.charAt(0).toUpperCase() + patient.gender?.slice(1) || "Unknown"}
                            {" · "}
                            Birth Date: {patient.birthDate || "N/A"}
                          </div>
                          <div className="text-muted small">
                            {patient.address?.[0]?.city}, {patient.address?.[0]?.state}
                          </div>
                        </div>
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                )}
              </div>
            )}

            {/* ICON + TOOLTIP */}
            <span
              ref={iconRef}
              className="d-inline-flex align-items-center"
              onMouseEnter={(e) => {
                e.stopPropagation();
                setShowTooltip(true);
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
                setShowTooltip(false);
              }}
              onClick={(e) => {
                e.stopPropagation(); // prevent double modal
                handleModalOpen();
              }}
            >
              <img
                src={patientIcon}
                alt="Patient Icon"
                style={{ cursor: "pointer", height: "32px", width: "32px" }}
              />
            </span>

            <Overlay target={iconRef.current} show={showTooltip} placement="bottom">
              {(props) => (
                <Tooltip id="patient-tooltip" {...props}>
                  View patient details
                </Tooltip>
              )}
            </Overlay>

            {/* Spinner for search */}
            {searching && <Spinner animation="border" size="sm" className="ms-2" />}

          </div>
        </Card.Header>


        <Card.Body>
          {error && <p style={{ color: "red" }}>{error}</p>}
          <ListGroup variant="flush">
            <ListGroup.Item className={styles.listItem}><strong>MRN:</strong> {mrn}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Name:</strong> {getPatientName(selectedPatient || patientData)}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>DOB:</strong> {displayPatient.birthDate}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Gender:</strong> {displayPatient.gender}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Marital Status:</strong> {displayPatient.maritalStatus?.text}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Address:</strong> {fullAddress || "N/A"}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Language Spoken:</strong> {displayPatient.communication?.[0]?.language?.text}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Phone Number:</strong> {displayPatient.telecom?.find(t => t.system === "phone")?.value}</ListGroup.Item>
          </ListGroup>
        </Card.Body>
      </Card>

      <ResourceViewerModal
        show={showModal}
        onClose={handleModalClose}
        resource={modalPatientDetails}
        //title={getPatientName(selectedPatient || patientData)}
        //icon={patientIcon}
        fhirClient={fhirClient}
      />
    </>
  );
}

export default PatientCard;
